import { Box } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import Pagination from '~/components/Pagination/Pagination';
import { SearchContentTable } from '~/components/SearchContentTable';
import { VIEW_OPTIONS } from '~/constants';
import { ISearchContentResult } from '~/searchApi/content';

import { PAGE_SIZE } from './SearchContent.constants';
import { SearchContentEmpty } from './components';
import { SearchContentEmptyPage } from './components/SearchContentEmptyPage';
import { SearchContentGrid } from './components/SearchContentGrid';
import { useSearchContentStore } from './useSearchContentStore';

export interface ISearchContentComponentProps {
  clear: () => void;
  onClickItem?: (content: ISearchContentResult) => void;
  onPagination: (page: number) => void;
  page: string;
  platforms?: string[];
  query?: string;
  results: ISearchContentResult[];
  totalItems: number;
  lastPage?: number /**
   * When provided, this value is used instead of calculating pages from totalItems.
   * This helps handle cases where the API's pagination logic differs from client-side calculations.
   */;
  view: VIEW_OPTIONS;
}

export const SearchContentComponent: React.FC<ISearchContentComponentProps> = ({
  onClickItem,
  onPagination,
  page,
  platforms,
  query,
  results,
  totalItems,
  lastPage,
  view,
}) => {
  const totalPages: number = lastPage ?? Math.ceil(totalItems / PAGE_SIZE);
  const router = useRouter();

  const { query: routerQuery } = router;

  const { hasFiltersApplied, setHasFiltersApplied } = useSearchContentStore();

  // Look for filters applied
  useEffect(() => {
    setHasFiltersApplied(
      !!routerQuery.postedAfter ||
        !!query ||
        routerQuery.ads === 'true' ||
        !!routerQuery.creator ||
        !!routerQuery.platform,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHasFiltersApplied]);

  if (results.length) {
    return (
      <Box>
        {view === VIEW_OPTIONS.grid ? (
          <>
            <SearchContentGrid contents={results} onClickItem={onClickItem} />
          </>
        ) : (
          <SearchContentTable
            contents={results}
            platforms={platforms}
            query={query}
            onClickItem={onClickItem}
          />
        )}

        {totalPages > 1 && (
          <Pagination
            page={parseInt(page, 10)}
            count={totalPages}
            onChange={onPagination}
            marginTop={6}
          />
        )}
      </Box>
    );
  }

  if (hasFiltersApplied) {
    return <SearchContentEmpty />;
  }

  return <SearchContentEmptyPage />;
};
